import React, { useContext } from 'react';
import { Group } from '@visx/group';
import { Margin, WithMargin } from './Chart.types';
import { useChartContainerContext } from './ChartContainer';
import { useChartTooltipContext } from './tooltip/ChartTooltipProvider';

type ChartSurfaceContextType = {
  surfaceRef: React.RefObject<SVGSVGElement | null>;
  innerWidth: number;
  innerHeight: number;
} & WithMargin;

const ChartSurfaceContext = React.createContext<ChartSurfaceContextType | undefined>(undefined);

export const ChartSurface = ({
  children,
  margin = { bottom: 0, left: 0, right: 0, top: 0 },
}: React.PropsWithChildren<{ margin?: Margin }>) => {
  const { height, width } = useChartContainerContext();
  const surfaceRef = React.useRef<SVGSVGElement | null>(null);
  const { updatePosition, isPinned } = useChartTooltipContext();

  const innerWidth = Math.max(width - margin.left - margin.right, 0);
  const innerHeight = Math.max(height - margin.top - margin.bottom, 0);

  const handlePointerMove = React.useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (isPinned) return;
      updatePosition(e.clientX, e.clientY);
    },
    [isPinned, updatePosition]
  );

  const value = React.useMemo(
    () => ({
      innerWidth,
      innerHeight,
      margin: {
        bottom: margin.bottom,
        left: margin.left,
        right: margin.right,
        top: margin.top,
      },
      surfaceRef,
    }),
    [innerWidth, innerHeight, margin.bottom, margin.left, margin.right, margin.top, surfaceRef]
  );

  return (
    <ChartSurfaceContext.Provider value={value}>
      <svg style={{ width: '100%', height: '100%', pointerEvents: 'auto' }} ref={surfaceRef} onPointerMove={handlePointerMove}>
        <Group top={margin.top} left={margin.left}>
          {children}
        </Group>
      </svg>
    </ChartSurfaceContext.Provider>
  );
};

export const useChartSurfaceContext = () => {
  const ctx = useContext(ChartSurfaceContext);
  if (!ctx) throw new Error('useChartSurfaceContext must be used within a ChartSurface');
  return ctx;
};
